import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, animate } from 'framer-motion';

/**
 * AnimatedCurve Component
 * 
 * Dessine une courbe de Bézier animée en arrière-plan
 * avec un point qui voyage le long de la courbe.
 * Représente visuellement le concept de "moment opportun".
 */
const AnimatedCurve = () => {
  const progress = useMotionValue(0);
  const springProgress = useSpring(progress, { damping: 30, stiffness: 100 });
  const pathRef = useRef(null);
  
  // Animate the point along the path
  useEffect(() => {
    const animation = animate(progress, 1, {
      duration: 8,
      repeat: Infinity,
      repeatType: 'loop',
      ease: 'linear',
    });
    
    return () => animation.stop();
  }, [progress]);
  
  // Calculate point position on bezier curve
  const getPointOnCurve = (t) => {
    // Bezier curve control points (normalized 0-1)
    const p0 = { x: 0.05, y: 0.7 };
    const p1 = { x: 0.25, y: 0.2 };
    const p2 = { x: 0.75, y: 0.8 };
    const p3 = { x: 0.95, y: 0.3 };
    
    const x = Math.pow(1-t, 3) * p0.x + 
              3 * Math.pow(1-t, 2) * t * p1.x + 
              3 * (1-t) * Math.pow(t, 2) * p2.x + 
              Math.pow(t, 3) * p3.x;
    
    const y = Math.pow(1-t, 3) * p0.y + 
              3 * Math.pow(1-t, 2) * t * p1.y + 
              3 * (1-t) * Math.pow(t, 2) * p2.y + 
              Math.pow(t, 3) * p3.y;
    
    return { x: x * 100, y: y * 100 };
  };
  
  // Key inflection points on the curve
  const keyPoints = [0.25, 0.5, 0.75];
  
  return (
    <svg
      className="w-full h-full opacity-40"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      {/* Main curve path */}
      <motion.path
        ref={pathRef}
        d="M 5 70 C 25 20, 75 80, 95 30"
        fill="none"
        stroke="rgba(42, 59, 143, 0.3)"
        strokeWidth="0.3"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, ease: 'easeInOut' }}
      />
      
      {/* Key inflection points */}
      {keyPoints.map((t, index) => {
        const point = getPointOnCurve(t);
        return (
          <motion.circle
            key={index}
            cx={point.x}
            cy={point.y}
            r="0.8"
            fill="rgba(255, 255, 255, 0.2)"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 2 + index * 0.3, duration: 0.5 }}
          />
        );
      })}
      
      {/* Moving point along the curve */}
      <motion.circle
        cx="0"
        cy="0"
        r="1.2"
        fill="#2A3B8F"
        style={{
          cx: springProgress.get() ? getPointOnCurve(springProgress.get()).x : 5,
          cy: springProgress.get() ? getPointOnCurve(springProgress.get()).y : 70,
        }}
      >
        <animate
          attributeName="cx"
          values="5;25;55;75;95;75;55;25;5"
          dur="8s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="cy"
          values="70;30;55;45;30;45;55;30;70"
          dur="8s"
          repeatCount="indefinite"
        />
      </motion.circle>
      
      {/* Glow effect for moving point */}
      <motion.circle
        cx="0"
        cy="0"
        r="3"
        fill="rgba(42, 59, 143, 0.3)"
      >
        <animate
          attributeName="cx"
          values="5;25;55;75;95;75;55;25;5"
          dur="8s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="cy"
          values="70;30;55;45;30;45;55;30;70"
          dur="8s"
          repeatCount="indefinite"
        />
      </motion.circle>
    </svg>
  );
};

export default AnimatedCurve;
